import SText from '../../../components/opinionless/SText'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Select from 'react-select'
import sSelectStyle from 'styles/select'

import { useTranslation } from '../../../i18n'
import { useSendConfirmationMutation } from 'types/graphql'
import SLink from 'components/opinionless/SLink'
import { SStyled, useSTheme } from 'styles/theme'
import SBox from '../../../components/opinionless/SBox'
import SFlex from '../../../components/opinionless/SFlex'
import SInputError from 'components/opinionless/SInputError'
import SInputLabel from 'components/opinionless/SInputLabel'
import { useAddApplicationAnswersMutation } from '../../../types/graphql'
import { ga4_useTitle } from 'utils/ga4'

interface Props {
    setCurrentStep?: (number) => void
    applicationId: string
    fontFamily: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const InputRow = SStyled(SFlex)`
    flex-direction: row;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
        }
    `}
`
const assistanceOrgs = [
    'community_action_partnership',
    'child_parent_institute',
    'corazón_healdsburg',
    'petaluma_people_services_center',
    'la_luz_center',
    'river_to_coast_childrens_services',
    'community_baptist_church',
    'west_county_health_center',
]

const ApplicationStep4: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const [sendConfirmation] = useSendConfirmationMutation()

    const { errors, setError, control } = useForm()
    const [addApplicationAnswers, { loading }] = useAddApplicationAnswersMutation()

    const householdMembersOptions = assistanceOrgs.map((organization) => ({
        value: organization,
        label: t(organization),
    }))

    const fontFamily = props.fontFamily

    ga4_useTitle('Step 4')

    useEffect(() => {
        sendConfirmation({
            variables: {
                applicationDetailsId: props.applicationId,
            },
        })
    }, [window])

    return (
        <Container>
            <SText
                textAlign={'center'}
                fontWeight={'bold'}
                fontSize={6}
                mb={5}
                color="black"
                fontFamily={fontFamily.header}
            >
                {t('application_submitted')}
            </SText>
            <SBox sx={{ textAlign: 'center' }} mt={5} mb={5}>
                <img style={{ textAlign: 'center', width: 'auto', height: '100%' }} src="/submitted-application.png" />
            </SBox>
            <InputRow>
                <SText
                    textAlign={'left'}
                    fontWeight={'bold'}
                    fontSize={5}
                    mb={5}
                    color="black"
                    fontFamily={fontFamily.body}
                >
                    {t('application_number')} <strong>#{atob(props.applicationId).split(':')[1]}</strong>
                </SText>
            </InputRow>
            <InputRow>
                <SText
                    fontSize={4}
                    textAlign={'left'}
                    color="gray400"
                    mb={6}
                    mt={7}
                    sx={{ whiteSpace: 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: t('application_confirmation_text') }}
                    fontFamily={fontFamily.body}
                ></SText>
            </InputRow>
            <SBox flex={1} style={{ marginBottom: '20px' }}>
                <SInputLabel
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        color: '#202020',
                        fontWeight: 'bold',
                        fontFamily: fontFamily.body,
                    }}
                >
                    {t('sonoma_assistance_from_organizations')}
                </SInputLabel>
                <Controller
                    name="organizationAssistance"
                    control={control}
                    defaultValue={'organizationAssistance'}
                    render={(propsSelect: any) => {
                        const { onChange, value } = propsSelect
                        return (
                            <Select
                                name="organizationAssistanceField"
                                id="organizationAssistanceField"
                                options={householdMembersOptions}
                                placeholder={t('sonoma_income_check_household_size_placeholder')}
                                components={{ IndicatorSeparator: () => null }}
                                onChange={(option: any) => {
                                    onChange(option)
                                    addApplicationAnswers({
                                        variables: {
                                            answers: [
                                                {
                                                    personApplicationId: props.applicationId,
                                                    category: 'ASSISTANCE_ORGS',
                                                    response: option.value,
                                                },
                                            ],
                                        },
                                    }).then((response) => {
                                        if (response.data.addApplicationAnswers.success === false) {
                                            setError('organizationAssistance', { type: 'oops', message: t('error') })
                                        }
                                    })
                                }}
                                value={value}
                                styles={{
                                    ...sSelectStyle(theme),
                                    container: (provided, state) => ({
                                        ...sSelectStyle(theme).container(provided, state),
                                        flex: 2,
                                    }),
                                    control: (provided, state) => ({
                                        ...sSelectStyle(theme).control(provided, state),
                                        fontFamily: fontFamily.body,
                                    }),
                                    option: (provided, state) => ({
                                        ...sSelectStyle(theme).option(provided, state),
                                        fontFamily: fontFamily.body,
                                    }),
                                }}
                                maxMenuHeight={100}
                            />
                        )
                    }}
                />
                {errors.organizationAssistance && (
                    <SInputError forElementId={'organizationAssistanceField'}>
                        {t(errors.organizationAssistance?.message)}
                    </SInputError>
                )}
            </SBox>
            <SLink
                href="https://first5sonomacounty.org/wp-content/uploads/2021/11/FAQS-GBI-1.pdf"
                color={'black'}
                target={'_blank'}
            >
                <SText fontFamily={fontFamily.body} fontWeight={'bold'} fontSize={3} textAlign={'center'}>
                    {t('questions_visit_faq')}
                </SText>
            </SLink>
        </Container>
    )
}

export default ApplicationStep4
